import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  Button,
  Callout,
  FormGroup,
  InputGroup,
  Intent,
} from "@blueprintjs/core";

import { isValidWorkEmail, prettifyAuthError, requestPasswordReset } from "../lib/auth";
import { SystemUseNotification } from "../components/system-use-notification";

export function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailTouched = email.length > 0;
  const emailValid = isValidWorkEmail(email);
  const emailHelper =
    emailTouched && !emailValid
      ? "Enter a valid email address."
      : undefined;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      navigate(`/reset-password?email=${encodeURIComponent(email)}`, { replace: true });
    } catch (err) {
      setError(prettifyAuthError((err as Error).message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="forgot-password" className="ic-auth-surface" style={shellStyle}>
      <div className="ic-auth-card" style={cardStyle}>
        <div style={brandStyle}>
          <span className="ic-auth-logo" role="img" aria-label="IronCase" />
          <div style={{ fontSize: 12, color: "var(--bp-text-muted)" }}>
            Reset password
          </div>
        </div>

        <form onSubmit={onSubmit} style={{ marginTop: 24 }}>
          <FormGroup
            label="Email"
            labelFor="email"
            intent={emailHelper ? Intent.DANGER : Intent.NONE}
            helperText={emailHelper ?? "We'll send a verification code to this address."}
          >
            <InputGroup
              id="email"
              data-testid="forgot-password-email"
              type="email"
              placeholder="you@your-agency.gov"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon="envelope"
              autoFocus
              required
              autoComplete="email"
              intent={emailHelper ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>

          {error && (
            <Callout intent={Intent.DANGER} icon="error" style={{ marginBottom: 12 }}>
              {error}
            </Callout>
          )}

          <Button
            type="submit"
            data-testid="forgot-password-submit"
            intent={Intent.PRIMARY}
            text="Send verification code"
            rightIcon="envelope"
            fill
            large
            loading={submitting}
            disabled={!email || !emailValid}
          />
        </form>

        <div style={{ marginTop: 12, textAlign: "center" }}>
          <Link
            to="/login"
            data-testid="forgot-password-back-to-login"
            style={{
              fontSize: 12,
              color: "var(--bp-text-muted)",
              textDecoration: "none",
            }}
          >
            Back to sign in
          </Link>
        </div>

        {/* CJIS §5.5.2 requires the system-use notification on every surface
            where a person interacts with the system before being granted
            access — not only the sign-in form. This page carried the condensed
            footer alone, so someone arriving from a password email never saw
            the full notice. Passive banner (no acknowledgement): there is no
            credential submission here to gate on it. */}
        <SystemUseNotification variant="banner" />
        <SystemUseNotification variant="footer" />
      </div>
    </div>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(circle at 30% 20%, var(--bp-bg-elev-1) 0%, var(--bp-bg) 60%)",
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  background: "var(--bp-bg-elev-1)",
  border: "1px solid var(--bp-border)",
  borderRadius: 8,
  padding: 28,
  // Shadow lives in _login.scss (.ic-auth-card) so it can differ per theme —
  // an inline value would win over the stylesheet and defeat that rule.
};

const brandStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  paddingBottom: 8,
  borderBottom: "1px solid var(--bp-border-soft)",
};

